import { Briefcase, Buildings, Gear, House, Newspaper, Star, Tag, UserCircle, Users } from '@myhome/ui/icons';
import { activeNavHref, SideNav } from '@myhome/ui/patterns/side-nav';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { PlaygroundBadge } from '../components/playground-badge';
import { authClient, sessionQuery } from '../lib/auth-client';

export const Route = createFileRoute('/_shell')({
  /**
   * Signed out goes to /login and comes back here afterwards; signed in as
   * anything but ADMIN gets the no-access screen. This is UX only: the API
   * checks the session and the role on every request.
   */
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session === null) throw redirect({ to: '/login', search: { redirect: location.href } });
    if (session.user.role !== 'ADMIN') throw redirect({ to: '/no-access' });
  },
  component: ShellLayout,
});

/**
 * Back office shell: the rail from 768px, a scrollable top row below that
 * (D30). Desktop first, but Demi approves providers from her phone, so it has
 * to stay usable at 390px (apps/admin/CLAUDE.md).
 */
const NAV = [
  { href: '/', label: 'Overview', icon: House },
  { href: '/providers', label: 'Providers', icon: Buildings },
  { href: '/members', label: 'Members', icon: Users },
  { href: '/agents', label: 'Agents', icon: UserCircle },
  { href: '/jobs', label: 'Jobs', icon: Briefcase },
  { href: '/reviews', label: 'Reviews', icon: Star },
  { href: '/content', label: 'Content', icon: Newspaper },
  { href: '/categories-areas', label: 'Categories and areas', icon: Tag },
  { href: '/settings', label: 'Settings', icon: Gear },
];

function ShellLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const signOut = async () => {
    await authClient.signOut();
    queryClient.clear();
    await navigate({ to: '/login', replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background md:flex-row">
      <SideNav
        badge={<PlaygroundBadge />}
        label="Back office"
        phone="top-row"
        items={NAV}
        activeHref={activeNavHref(NAV, pathname)}
        user={session === null ? undefined : { name: session.user.name, detail: session.user.email }}
        onSignOut={signOut}
        renderLink={({ href, key, children, ...rest }) => (
          <Link key={key} to={href} {...rest}>
            {children}
          </Link>
        )}
      />

      <main className="min-w-0 flex-1">
        {/*
          Wider than the member app: these are tables, and a back office read
          at 1600px should not leave half the window empty (DESIGN.md "Page
          templates").
        */}
        <div className="mx-auto max-w-[80rem] px-4 py-5 lg:px-6 lg:py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
